import { EnvironmentProviders, effect, inject, provideAppInitializer } from '@angular/core';

import { environment } from '@environments/environment';

import { CommandPaletteService, PaletteItem } from '../../core/services/command-palette.service';
import { AgentChatService } from './agent-chat.service';

/**
 * Registers the "Investigate with Copilot" command-palette entry — the M1 entry
 * point into the interactive copilot. Wired into the app config's providers.
 *
 * TWO gates, so the feature lands (and stays) dark until BOTH are set:
 *   - `environment.features.enableCopilotChat` — the build-time HARD kill-switch.
 *     When false THIS initializer wires nothing (no palette entry, no effects).
 *     GAP-CP-021-B comment truth-up: the surface is NOT "provably absent from
 *     the build" — `AppComponent` still imports/mounts `<app-copilot-chat>` and
 *     injects `AgentChatService` unconditionally; the same build flag merely
 *     keeps them inert (`enabled()` is false, the template renders nothing).
 *   - the runtime `CopilotStatusService.status().enabled` (folded into
 *     `AgentChatService.enabled`) — the operational toggle, driven by the
 *     backend `COPILOT_CHAT_ENABLED` env flip with NO frontend rebuild.
 *
 * The entry is registered REACTIVELY via an effect on `chat.enabled()` so it only
 * appears once the backend reports the copilot enabled — never a visible-but-
 * broken entry — and WITHDRAWN again (GAP-CP-021-A) the moment the gate closes
 * (runtime kill-switch flip, logout, permission loss), so ⌘K never surfaces an
 * action that would dead-click.
 */
export function provideCopilotChat(): EnvironmentProviders {
  return provideAppInitializer(() => {
    // HARD kill-switch: when the build flag is off, wire nothing at all.
    if (environment.features.enableCopilotChat !== true) {
      return;
    }
    const palette = inject(CommandPaletteService);
    const chat = inject(AgentChatService);

    // Register the explicit palette action; opening a panel is not navigation.
    const copilotItem: PaletteItem = {
      id: 'action:copilot-investigate',
      label: 'Investigate with Copilot',
      kind: 'action',
      description: 'Ask the read-only ops copilot about positions, risk, PnL, and orders',
      keywords: ['copilot', 'investigate', 'ai', 'assistant', 'ask', 'chat'],
      run: () => chat.openPanel(),
    };

    let registered = false;
    effect(() => {
      // `chat.enabled()` == build-flag (already true here) AND runtime status
      // AND the caller's copilot:chat:read authorization. Register when the
      // gate opens; withdraw (GAP-CP-021-A) when it closes again so the entry
      // tracks availability in BOTH directions.
      const enabled = chat.enabled();
      if (enabled && !registered) {
        registered = true;
        palette.registerItems([copilotItem]);
      } else if (!enabled && registered) {
        registered = false;
        palette.unregisterItems([copilotItem.id]);
      }
    });

  });
}
